import { InfoCircle, SearchLg, Trash02 } from "@untitledui/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useProducts } from "@/api/products";
import { Input } from "@/components/base/input/input";
import { Toggle } from "@/components/base/toggle/toggle";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { useCurrency } from "@/hooks/use-currency";
import { useClearableDebounce } from "@/hooks/use-debounce";
import type { CreateCollabFormValues, ProductEntry } from "../create-collab-group-modal";

function formatPrice(prices: number[], currency: string): string {
  if (!prices.length) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(Math.min(...prices));
}

const QUICK_RATES = ["10", "12", "15", "18"];

export const ProductStep2 = () => {
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<CreateCollabFormValues>();

  const products = watch("products");

  const [search, setSearch] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const { value: debouncedSearch } = useClearableDebounce(search);
  const { data: allProducts } = useProducts(debouncedSearch || undefined);
  const currency = useCurrency();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedIds = useMemo(() => new Set(products.map((p) => p.id)), [products]);

  const filteredProducts = useMemo(() => {
    if (!allProducts) return [];
    return allProducts.filter((p) => !selectedIds.has(p.productId));
  }, [allProducts, selectedIds]);

  const addProduct = useCallback(
    (product: { productId: string; productName: string; thumbnail: string | null; prices: number[] }) => {
      const entry: ProductEntry = {
        id: product.productId,
        name: product.productName,
        thumbnail: product.thumbnail,
        price: formatPrice(product.prices, currency),
        commissionRate: "",
        shopAdsEnabled: false,
        shopAdsCommission: "",
      };
      setValue("products", [...products, entry], { shouldValidate: true });
      setSearch("");
      setIsDropdownOpen(false);
    },
    [products, setValue, currency],
  );

  const removeProduct = useCallback(
    (index: number) => {
      setValue(
        "products",
        products.filter((_, i) => i !== index),
        { shouldValidate: true },
      );
    },
    [products, setValue],
  );

  const updateProduct = useCallback(
    (index: number, patch: Partial<ProductEntry>) => {
      setValue(
        "products",
        products.map((p, i) => (i === index ? { ...p, ...patch } : p)),
        { shouldValidate: true },
      );
    },
    [products, setValue],
  );

  const applyDefaultRate = useCallback(
    (rate: string) => {
      setValue(
        "products",
        products.map((p) => ({ ...p, commissionRate: rate })),
      );
    },
    [products, setValue],
  );

  const productsError = errors.products as { message?: string } | undefined;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-base font-semibold text-primary">Products</p>
        <p className="text-sm text-quaternary mt-1">Add products and set commission rates</p>
      </div>

      <div ref={searchRef} className="w-full relative">
        <Input
          icon={SearchLg}
          placeholder="Search for a product to add…"
          value={search}
          onChange={(v) => {
            setSearch(v);
            setIsDropdownOpen(true);
          }}
          onFocus={() => setIsDropdownOpen(true)}
        />
        {isDropdownOpen && filteredProducts.length > 0 && (
          <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-48 overflow-y-auto rounded-lg border border-primary bg-primary shadow-lg">
            {filteredProducts.map((product) => (
              <button
                key={product.productId}
                type="button"
                onClick={() => addProduct(product)}
                className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-primary_hover"
              >
                {product.thumbnail ? (
                  <img src={product.thumbnail} alt="" className="size-8 rounded object-cover" />
                ) : (
                  <div className="size-8 rounded bg-secondary" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-primary">{product.productName}</p>
                  <p className="text-xs text-tertiary">{formatPrice(product.prices, currency)}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-primary">Set Default Commission Rate</p>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            {QUICK_RATES.map((rate) => (
              <button
                key={rate}
                type="button"
                onClick={() => applyDefaultRate(rate)}
                className="bg-primary border border-primary rounded-md px-3 py-2 text-sm font-semibold text-secondary hover:bg-secondary transition-colors"
              >
                {rate}%
              </button>
            ))}
          </div>
          <div className="flex items-center justify-center gap-1 w-[74px] border border-primary rounded-md px-3 py-2 bg-primary">
            <input
              type="number"
              min={0}
              max={100}
              placeholder="0"
              className="w-8 text-sm text-secondary bg-transparent outline-none text-right [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              onBlur={(e) => {
                if (e.target.value) applyDefaultRate(e.target.value);
              }}
            />
            <span className="text-sm text-secondary">%</span>
          </div>
        </div>
      </div>

      {products.map((product, index) => {
        const commissionRateError = (
          errors.products as { [i: number]: { commissionRate?: { message?: string } } } | undefined
        )?.[index]?.commissionRate?.message;

        return (
          <div key={product.id} className="flex flex-col">
            <div className="bg-secondary border-t border-l border-r border-primary rounded-t-md flex items-center gap-3 p-3">
              {product.thumbnail ? (
                <img src={product.thumbnail} alt="" className="size-12 shrink-0 rounded-sm object-cover" />
              ) : (
                <div className="size-12 shrink-0 rounded-sm bg-tertiary" />
              )}
              <div className="flex-1 min-w-0">
                <p className="truncate text-sm font-medium text-primary">{product.name}</p>
                <p className="text-sm text-tertiary">{product.price}</p>
              </div>
              <div className="flex flex-col items-end gap-0.5 shrink-0">
                <div className="flex items-center gap-0.5">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={product.commissionRate}
                    placeholder="0"
                    onChange={(e) => updateProduct(index, { commissionRate: e.target.value })}
                    className="w-[50px] border border-primary rounded-md px-3 py-1.5 text-sm text-center bg-primary outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  />
                  <button
                    type="button"
                    onClick={() => removeProduct(index)}
                    className="size-8 flex items-center justify-center rounded-md hover:bg-tertiary transition-colors"
                  >
                    <Trash02 className="size-4 text-quaternary" />
                  </button>
                </div>
                {commissionRateError && <p className="text-xs text-error-primary">{commissionRateError}</p>}
              </div>
            </div>
            <div className="bg-secondary border border-primary rounded-b-md h-11 px-3 flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <span className="text-sm text-quaternary">Shop Ads Commission</span>
                <Tooltip title="Additional commission for TikTok Shop Ads" arrow placement="top">
                  <TooltipTrigger>
                    <InfoCircle className="size-4 text-quaternary" />
                  </TooltipTrigger>
                </Tooltip>
              </div>
              <div className="flex items-center gap-2">
                <Toggle
                  size="sm"
                  isSelected={product.shopAdsEnabled}
                  onChange={(val) => updateProduct(index, { shopAdsEnabled: val })}
                />
                {product.shopAdsEnabled && (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={product.shopAdsCommission}
                      onChange={(e) => updateProduct(index, { shopAdsCommission: e.target.value })}
                      className="w-[50px] border border-primary rounded-md px-3 py-1.5 text-sm text-center bg-primary outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                    <span className="text-sm text-tertiary">%</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {productsError?.message && <p className="text-sm text-error-primary">{productsError.message}</p>}
    </div>
  );
};
